import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useMemberSurveillanceOverview from '../hooks/useMemberSurveillanceOverview';
import { Spinner } from '../components/ui';
import {
  buildDueActionCards,
  buildFamilyIntelligencePrompts,
  buildMelioBrief,
  buildOpenLoopCards,
  buildPlanActivity,
  buildSignalCards,
  formatDateLabel,
} from '../utils/memberEngagement';

export default function YourPlanPage() {
  const { currentUser } = useAuth();
  const { loading, latestPlan: plan, openPrompts: prompts, familyTree } = useMemberSurveillanceOverview(currentUser?.uid);

  const context = useMemo(() => ({
    profile: null,
    medicalDocCount: 0,
    wearableDocCount: 0,
    labResults: [],
    latestPlan: plan,
    openPrompts: prompts,
    familyTree,
  }), [familyTree, plan, prompts]);

  const dueActionCards = useMemo(() => buildDueActionCards(plan), [plan]);
  const signalCards = useMemo(() => buildSignalCards(context), [context]);
  const openLoopCards = useMemo(() => buildOpenLoopCards(context), [context]);
  const familyPrompts = useMemo(() => buildFamilyIntelligencePrompts(context), [context]);
  const brief = useMemo(() => buildMelioBrief(context), [context]);
  const activity = useMemo(() => buildPlanActivity(context), [context]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <section className="mb-8">
        <div className="rounded-3xl border p-8 md:p-10 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8">
            <div className="max-w-3xl">
              <p className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
                Clinician-reviewed prevention plan
              </p>
              <h1
                className="text-3xl md:text-5xl font-normal mb-4"
                style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
              >
                Your prevention record, reviewed over time.
              </h1>
              <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
                This view brings together the context Melio is tracking for you and any clinician-reviewed next steps that
                have been released to your account.
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
                It is designed to support longitudinal prevention planning. It is not a substitute for urgent care,
                emergency advice, or confirmatory diagnostic follow-up where that is needed.
              </p>
            </div>

            <div className="rounded-3xl p-6" style={{ backgroundColor: 'rgba(79, 93, 24, 0.1)' }}>
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                Plan status
              </p>
              <p className="text-lg font-medium mb-3" style={{ color: 'var(--melio-charcoal)' }}>
                {plan?.status ?? 'Awaiting clinician draft'}
              </p>
              <div className="space-y-3 text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                <p>
                  <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Version:</span>{' '}
                  {plan ? plan.version : 'Pending'}
                </p>
                <p>
                  <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Open loops:</span>{' '}
                  {prompts.length}
                </p>
                <p>
                  <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Last update:</span>{' '}
                  {formatDateLabel(plan?.updatedAt)}
                </p>
                <p>
                  <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Family context:</span>{' '}
                  {familyTree?.status === 'published' ? 'Unlocked' : 'Awaiting clinician publication'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
            This screen shows plan content only after Melio has marked it as released to the patient account. Genetics,
            family history, and biomarker context may still require confirmatory testing, GP follow-up, or specialist review.
            If you have urgent symptoms, do not wait for a plan update.
          </p>
        </div>
      </section>

      {plan ? (
        <div className="space-y-6">
          <section className="grid md:grid-cols-3 gap-4">
            <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--melio-text-muted)' }}>
                Plan status
              </p>
              <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                {plan.status}
              </p>
            </div>
            <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--melio-text-muted)' }}>
                Current version
              </p>
              <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                {plan.version}
              </p>
            </div>
            <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid var(--melio-border)' }}>
              <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--melio-text-muted)' }}>
                Last updated
              </p>
              <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                {formatDateLabel(plan.updatedAt)}
              </p>
            </div>
          </section>

          <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--melio-sage)' }}>
                  {brief.title}
                </p>
                <h2
                  className="text-2xl md:text-3xl font-normal mb-3"
                  style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                >
                  Your plan, translated into plain English.
                </h2>
                <p className="text-sm md:text-base leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
                  {plan.patientSummary || plan.summary}
                </p>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
                  {brief.summary}
                </p>
              </div>
              <div className="rounded-3xl p-5 lg:max-w-sm" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                  Inputs reviewed
                </p>
                <div className="space-y-3 text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  <p>
                    <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Family history:</span>{' '}
                    {plan.familyHistorySummary || 'Pending richer context'}
                  </p>
                  <p>
                    <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Genetics:</span>{' '}
                    {plan.geneticContext?.vendor || 'No genetics context linked yet'}
                  </p>
                  <p>
                    <span className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>Clinician:</span>{' '}
                    {plan.clinicianName || 'Melio clinical team'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {brief.bullets.map((bullet) => (
                <div key={bullet} className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
                    {bullet}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <div className="flex items-center justify-between mb-5">
                <h2
                  className="text-2xl font-normal"
                  style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                >
                  Due next
                </h2>
                <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  Keep the plan moving
                </span>
              </div>
              <div className="space-y-3">
                {dueActionCards.map((item) => (
                  <div key={item.id} className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                        {item.title}
                      </p>
                      <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                        {item.status}
                      </span>
                    </div>
                    <p className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                      {item.detail}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                      Recommended cadence: {item.nextDueLabel}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <h2
                className="text-2xl font-normal mb-4"
                style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
              >
                Plan activity
              </h2>
              <div className="space-y-3">
                {activity.map((entry) => (
                  <div key={entry.id} className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)' }}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                        {entry.title}
                      </p>
                      <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                        {entry.dateLabel}
                      </span>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                      {entry.detail}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="grid lg:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
              <div className="flex items-center justify-between mb-5">
                <h2
                  className="text-2xl font-normal"
                  style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                >
                  Interpretation updates
                </h2>
                <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                  Signal to meaning
                </span>
              </div>
              <div className="space-y-4">
                {signalCards.map((card) => (
                  <div key={card.id} className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                      {card.eyebrow}
                    </p>
                    <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                      {card.title}
                    </p>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                      {card.detail}
                    </p>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                        {card.meta}
                      </p>
                      <Link to={card.path} className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
                        {card.ctaLabel}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-3xl border p-6 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h2
                    className="text-2xl font-normal"
                    style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                  >
                    Open loops inbox
                  </h2>
                  <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                    {openLoopCards.length} active
                  </span>
                </div>
                <div className="space-y-3">
                  {openLoopCards.map((item) => (
                    <div key={item.id} className="rounded-2xl p-4" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                          {item.title}
                        </p>
                        <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                          {item.urgency}
                        </span>
                      </div>
                      <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                        {item.detail}
                      </p>
                      <Link to={item.path} className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
                        {item.ctaLabel}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border p-6 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h2
                    className="text-2xl font-normal"
                    style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
                  >
                    Family intelligence
                  </h2>
                  <Link to="/history" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
                    Refine details
                  </Link>
                </div>
                {familyPrompts.length > 0 ? (
                  <div className="space-y-3">
                    {familyPrompts.map((prompt) => (
                      <div key={prompt.id} className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)' }}>
                        <p className="font-medium mb-1" style={{ color: 'var(--melio-charcoal)' }}>
                          {prompt.title}
                        </p>
                        <p className="text-sm mb-3" style={{ color: 'var(--melio-text-muted)' }}>
                          {prompt.detail}
                        </p>
                        <Link to={prompt.path} className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
                          {prompt.ctaLabel}
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                    Melio will place high-value family follow-ups here when they can improve the interpretation of your plan.
                  </p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
            <h2
              className="text-2xl font-normal mb-4"
              style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
            >
              Recommended cadence
            </h2>
            <div className="space-y-3">
              {plan.items.map((item, index) => (
                <div
                  key={`${item.action}-${index}`}
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                    <p className="text-sm font-medium" style={{ color: 'var(--melio-charcoal)' }}>
                      {item.modality}: {item.action}
                    </p>
                    <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                      {item.nextDue ? `Next due ${item.nextDue}` : 'Schedule pending'}
                    </span>
                  </div>
                  <p className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                    Frequency: {item.frequency}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
                    {item.reason}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid md:grid-cols-3 gap-4">
            <Link
              to="/results"
              className="rounded-xl border p-5 transition-opacity hover:opacity-90"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
            >
              <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                Upload new data
              </p>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                Add bloods, genetics reports, or wearable exports so the plan can keep evolving.
              </p>
            </Link>
            <Link
              to="/history"
              className="rounded-xl border p-5 transition-opacity hover:opacity-90"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
            >
              <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                Update health history
              </p>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                Refine the family story and clinical context sitting underneath your current cadence.
              </p>
            </Link>
            {/* Was /enrollment — the public apply-and-pay funnel. A member
                following "book a follow-up" landed in a new-patient signup
                form outside the member shell. */}
            <Link
              to="/testing"
              className="rounded-xl border p-5 transition-opacity hover:opacity-90"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
            >
              <p className="font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>
                Book follow-up consult
              </p>
              <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                Use clinician time when a result, escalation, or decision needs deeper discussion.
              </p>
            </Link>
          </section>
        </div>
      ) : (
        <div className="border rounded-xl p-8 text-center bg-white" style={{ borderColor: 'var(--melio-border)' }}>
          <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
            Your clinician-reviewed results and surveillance plan will appear here after the first assessment and data review.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/results"
              className="px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              Upload data
            </Link>
            <Link
              to="/history"
              className="px-5 py-3 rounded text-sm font-medium border transition-opacity hover:opacity-90"
              style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
            >
              Update health history
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
